import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { BrandMark } from "../components/Brand";
import { PORTAL_NAV } from "./nav";
import { SignOutItem } from "../components/SignOutItem";
import { ThemeToggle } from "../components/layout/ThemeToggle";
import { Button } from "../components/ui/button";
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  TooltipProvider,
} from "../components/ui/primitives";
import { cn } from "../lib/utils";
import { useMe } from "./auth";

// Sourced from portal/nav.ts so the header, the mobile strip and the profile
// menu cannot drift apart. The catalog sits before "Raise a ticket" on purpose:
// browsing what IT offers is the better first move, and /new stays for people
// who already know what they want to say.
const links = PORTAL_NAV.filter((item) =>
  ["/", "/tickets", "/requests", "/catalog", "/kb"].includes(item.to),
);

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** Routes that browse rather than read.
 *
 * The portal is 64rem wide because that is a comfortable measure for a ticket
 * conversation, a form and an article — the pages people read. A catalog is
 * scanned, not read, and at 64rem its card grid never gets past two columns and
 * leaves half a laptop screen empty. These routes get the wider container; the
 * header tracks it so the brand does not drift away from the content edge.
 */
const WIDE_ROUTES = ["/catalog", "/kb", "/tickets", "/requests"];

export function PortalLayout() {
  const { pathname } = useLocation();

  if (pathname === "/login") {
    return (
      <TooltipProvider delayDuration={300}>
        <Outlet />
      </TooltipProvider>
    );
  }

  return <PortalChrome pathname={pathname} />;
}

function PortalChrome({ pathname }: { pathname: string }) {
  const { data: me } = useMe();
  const wide = WIDE_ROUTES.some((route) => pathname.startsWith(route));
  const container = wide ? "max-w-7xl" : "max-w-5xl";

  return (
    // The console's AppShell has always provided this; the portal never used a
    // tooltip until the knowledge base needed to explain an unavailable field.
    <TooltipProvider delayDuration={300}>
      <div className="min-h-screen bg-surface text-foreground">
        {/* WCAG 2.4.1. The nav is long enough that skipping it matters. */}
        <a
          href="#portal-main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-popover focus:px-3 focus:py-2 focus:text-sm focus:shadow-popover"
        >
          Skip to content
        </a>

        <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div
            className={cn(
              "mx-auto flex h-14 items-center gap-3 px-6",
              container,
            )}
          >
            <span className="flex items-center gap-2 font-semibold tracking-tight">
              <BrandMark className="size-6" alt="" />
              <span className="hidden sm:inline">Logic Planet ITSM</span>
            </span>
            {/* The single entry point to the 25th Anniversary experience. A
                chip rather than a nav row: it is an invitation, not a
                destination people return to, and the anniversary page owns the
                only link back out to the Helpdesk. */}
            <Link
              to="/anniversary"
              className="hidden items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-primary transition-colors hover:bg-primary/10 sm:inline-flex"
            >
              <span aria-hidden>🎉</span>
              25 Years
            </Link>

            <nav
              className="ml-2 hidden items-center gap-0.5 md:flex"
              aria-label="Portal"
            >
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    cn(
                      "rounded-md px-2.5 py-1.5 text-sm transition-colors",
                      isActive
                        ? "bg-accent font-medium text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="ml-auto flex items-center gap-1">
              <ThemeToggle />
              {me && (
                <>
                  <Separator orientation="vertical" className="mx-1 h-5" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 pl-1 pr-2"
                      >
                        <Avatar className="size-6">
                          <AvatarFallback>
                            {initialsOf(me.name || me.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="hidden max-w-[8rem] truncate sm:inline">
                          {me.name || me.email}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="truncate text-sm font-medium">
                          {me.name || me.email}
                        </p>
                        {me.organization && (
                          <p className="truncate text-xs text-muted-foreground">
                            {me.organization}
                          </p>
                        )}
                      </div>
                      <DropdownMenuSeparator />
                      {/* The destinations that do not earn a slot in the top
                          strip. Sourced from the same list so nothing is
                          reachable only by typing a URL. */}
                      {PORTAL_NAV.filter(
                        (item) => !links.some((link) => link.to === item.to),
                      ).map((item) => (
                        <DropdownMenuItem key={item.to} asChild>
                          <NavLink to={item.to}>
                            <item.icon />
                            {item.label}
                          </NavLink>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <SignOutItem />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>

          {/* The nav collapses on narrow screens rather than disappearing. */}
          <nav
            className="flex gap-0.5 overflow-x-auto border-t border-border px-4 py-1.5 md:hidden"
            aria-label="Portal"
          >
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  cn(
                    "whitespace-nowrap rounded-md px-2.5 py-1 text-sm transition-colors",
                    isActive
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground",
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </header>

        <main id="portal-main" className={cn("mx-auto px-6 py-8", container)}>
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  );
}
